/**
 * 🌸 靈魂觀測台 Soul Dashboard v2.0
 *
 * 主要功能：
 * - LINE 群組 / 私訊監控
 * - 即時對話追蹤
 * - 群組成員數、對話統計
 * - 每日對話摘要
 */

import { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../lib/api';
import './SoulDashboard.css';

// Types
interface Space {
  id: string;
  type: 'private' | 'group' | 'family' | 'duo_group' | 'multi_group';
  name: string;
  icon?: string;
  relationship?: string;
  languagePref?: string;
  isActive?: boolean;
  lastMessage?: string;
  lastTime?: string;
  lastChatAt?: string;
  unread?: number;
  status?: 'online' | 'sleeping' | 'offline';
  hasAlert?: boolean;
  memberCount?: number;
  todayChats?: number;
  sourceType?: string;
  sourceId?: string;
  rules?: any;
  hasDaddy?: boolean;  // 是否有 Daddy 在群內
}

interface Alert {
  id: string;
  type: string;
  message: string;
  source: string;
  time: string;
  userId?: number;
  resolved?: boolean;
}

interface ChatMessage {
  id: string;
  time: string;
  userMessage: {
    name: string;
    text: string;
    avatar: string;
  };
  aiResponses: {
    text: string;
  }[];
  process: {
    tags: { text: string; type: 'action' | 'skill' | 'context' }[];
    thought: string;
  };
  stats: {
    duration: string;
    tokens: number;
    cost: string;
  };
}

interface SpaceContext {
  status: 'awake' | 'sleeping' | 'dreaming';
  lastChat: string;
  todayChats: number;
  emotionalTemp: number;
  skills: { name: string; active: boolean }[];
  cognition: {
    identity: string;
    mode: string;
    language: string;
    taboos: string;
  };
  dreamSeed?: {
    content: string;
    used: boolean;
  } | null;
  activePrompt?: string | null;
  rules?: string[];
}

interface DailySummary {
  id: number;
  date: string;
  messageCount: number;
  participantCount: number;
  summary: string;
  highlights: string[];
  mood: string;
  topics: string[];
  generatedAt: string;
  generatedBy: string;
}

interface DashboardStats {
  todayChats: number;
  activeUsers: number;
  lastDreamGenerated: string;
}

export default function SoulDashboard() {
  // State
  const [spaces, setSpaces] = useState<Space[]>([]);
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [chats, setChats] = useState<ChatMessage[]>([]);
  const [context, setContext] = useState<SpaceContext | null>(null);

  const [activeSpace, setActiveSpace] = useState<string>('');
  const [activeTab, setActiveTab] = useState<'chat' | 'cognition' | 'members' | 'summary'>('chat');
  const [mobilePanel, setMobilePanel] = useState<'spaces' | 'chat' | 'context' | 'alert'>('chat');
  const [summaries, setSummaries] = useState<DailySummary[]>([]);
  const [isGeneratingSummary, setIsGeneratingSummary] = useState(false);

  const [isConnected, setIsConnected] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch dashboard overview
  const fetchOverview = useCallback(async () => {
    try {
      const res = await apiFetch('/api/dashboard/overview');
      if (!res.ok) {
        throw new Error('無法載入監控台資料');
      }
      const data = await res.json();
      setSpaces(data.spaces || []);
      setAlerts(data.alerts || []);
      setStats(data.stats || null);

      // 自動選擇第一個空間
      if (data.spaces?.length > 0 && !activeSpace) {
        setActiveSpace(data.spaces[0].id);
      }

      setIsConnected(true);
      setError(null);
    } catch (err) {
      console.error('Dashboard overview error:', err);
      setError('連線失敗，請重新整理頁面');
      setIsConnected(false);
    } finally {
      setIsLoading(false);
    }
  }, [activeSpace]);

  // Fetch conversations for selected space
  const fetchConversations = useCallback(async (spaceId: string) => {
    if (!spaceId) return;
    try {
      // 使用新的 space API
      const res = await apiFetch(`/api/dashboard/space/${spaceId}/conversations?limit=20`);
      if (!res.ok) throw new Error('無法載入對話記錄');
      const data = await res.json();
      setChats(data.conversations || []);
    } catch (err) {
      console.error('Conversations error:', err);
      setChats([]);
    }
  }, []);

  // Fetch context for selected space
  const fetchContext = useCallback(async (spaceId: string) => {
    if (!spaceId) return;
    try {
      // 使用新的 space API
      const res = await apiFetch(`/api/dashboard/space/${spaceId}/context`);
      if (!res.ok) throw new Error('無法載入上下文');
      const data = await res.json();
      setContext(data);
    } catch (err) {
      console.error('Context error:', err);
      setContext(null);
    }
  }, []);

  // Fetch daily summaries for selected space
  const fetchSummaries = useCallback(async (spaceId: string) => {
    if (!spaceId) return;
    try {
      const res = await apiFetch(`/api/dashboard/spaces/${spaceId}/summaries?limit=14`);
      if (!res.ok) throw new Error('無法載入每日摘要');
      const data = await res.json();
      setSummaries(data.summaries || []);
    } catch (err) {
      console.error('Summaries error:', err);
      setSummaries([]);
    }
  }, []);

  // Generate daily summary manually
  const handleGenerateSummary = async () => {
    if (!activeSpace || isGeneratingSummary) return;
    setIsGeneratingSummary(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      const res = await apiFetch(`/api/dashboard/spaces/${activeSpace}/summary/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date: today })
      });
      if (!res.ok) throw new Error('摘要生成失敗');
      const data = await res.json();
      if (data.success) {
        alert(`📊 摘要生成完成！\n\n${data.summary?.summary || ''}`);
        await fetchSummaries(activeSpace);
      } else {
        alert(data.message || '該日期沒有對話記錄');
      }
    } catch (err) {
      console.error('Summary generation error:', err);
      alert('摘要生成失敗');
    } finally {
      setIsGeneratingSummary(false);
    }
  };


  // Initial load
  useEffect(() => {
    fetchOverview();

    // 定期刷新
    const interval = setInterval(fetchOverview, 30000);
    return () => clearInterval(interval);
  }, [fetchOverview]);

  // Load data when space changes
  useEffect(() => {
    if (activeSpace) {
      fetchConversations(activeSpace);
      fetchContext(activeSpace);
      fetchSummaries(activeSpace);
    }
  }, [activeSpace, fetchConversations, fetchContext, fetchSummaries]);

  const selectedSpace = spaces.find(s => s.id === activeSpace);
  const alertCount = alerts.length;

  // Mood emoji helper
  const getMoodEmoji = (mood: string) => {
    const moodMap: Record<string, string> = {
      'happy': '😊',
      'playful': '😄',
      'neutral': '😐',
      'serious': '🧐',
      'emotional': '🥹',
      'sad': '😢',
      'excited': '🤩'
    };
    return moodMap[mood] || '💬';
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="soul-dashboard">
        <div className="sd-loading">
          <div className="sd-loading-icon">🌸</div>
          <div className="sd-loading-text">載入中...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="soul-dashboard">
      {/* Header - 簡潔版 */}
      <header className="sd-header">
        <div className="sd-header-left">
          <h1 className="sd-header-title">靈魂觀測台</h1>
        </div>
        <div className="sd-header-actions">
          {alertCount > 0 && (
            <div className="sd-live-indicator sd-alert-indicator" title={`${alertCount} 個異常待處理`}>
              <span className="sd-live-dot sd-alert-dot"></span>
              <span>{alertCount} 異常</span>
            </div>
          )}
          <div className="sd-live-indicator">
            <span className={`sd-live-dot ${isConnected ? '' : 'offline'}`}></span>
            <span>{isConnected ? '連線中' : '已斷線'}</span>
          </div>
          {stats && (
            <div className="sd-stats-badge">
              <span>今日 {stats.todayChats} 則</span>
            </div>
          )}
          <button className="sd-btn" onClick={() => window.location.href = '/skills'}>
            🎯 Skill
          </button>
        </div>
      </header>

      {/* Error Banner */}
      {error && (
        <div className="sd-error-banner">
          ⚠️ {error}
          <button onClick={fetchOverview}>重試</button>
        </div>
      )}

      {/* Main Container */}
      <div className="sd-main-container">
        {/* Left Sidebar - Spaces List */}
        <aside className={`sd-spaces-sidebar ${mobilePanel === 'spaces' ? 'mobile-active' : ''}`}>
          <div className="sd-spaces-header">
            <div className="sd-spaces-title">LINE 群空間列表</div>
            <div className="sd-spaces-count">{spaces.length} 個</div>
          </div>

          <div className="sd-spaces-list">
            {/* 多人群組 (3人以上) */}
            {spaces.filter(s => s.type === 'multi_group').length > 0 && (
              <>
                <div className="sd-space-group-title">👥 多人群組</div>
                {spaces.filter(s => s.type === 'multi_group').map(space => (
                  <div
                    key={space.id}
                    className={`sd-space-item ${activeSpace === space.id ? 'active' : ''}`}
                    onClick={() => { setActiveSpace(space.id); setMobilePanel('chat'); }}
                  >
                    <div className="sd-space-info">
                      <div className="sd-space-name">
                        {space.name || '未命名群組'}
                        {space.hasDaddy && <span className="sd-daddy-badge">Daddy</span>}
                      </div>
                      <div className="sd-space-meta-line">
                        <span className="sd-member-count">{space.memberCount || '?'} 人</span>
                        <span className="sd-chat-count">{space.todayChats || 0} 則</span>
                      </div>
                    </div>
                    {alerts.some(a => (a as any).spaceId?.toString() === space.id) && (
                      <div className="sd-space-alert">!</div>
                    )}
                  </div>
                ))}
              </>
            )}

            {/* 雙人群組 (只有2人) */}
            {spaces.filter(s => s.type === 'duo_group').length > 0 && (
              <>
                <div className="sd-space-group-title">👫 雙人群</div>
                {spaces.filter(s => s.type === 'duo_group').map(space => (
                  <div
                    key={space.id}
                    className={`sd-space-item ${activeSpace === space.id ? 'active' : ''}`}
                    onClick={() => { setActiveSpace(space.id); setMobilePanel('chat'); }}
                  >
                    <div className="sd-space-info">
                      <div className="sd-space-name">
                        {space.name || '雙人群'}
                        {space.hasDaddy && <span className="sd-daddy-badge">Daddy</span>}
                      </div>
                      <div className="sd-space-meta-line">
                        <span className="sd-member-count">2 人</span>
                        <span className="sd-chat-count">{space.todayChats || 0} 則</span>
                      </div>
                    </div>
                    {alerts.some(a => (a as any).spaceId?.toString() === space.id) && (
                      <div className="sd-space-alert">!</div>
                    )}
                  </div>
                ))}
              </>
            )}

            {/* 家人 1對1 私訊 */}
            {spaces.filter(s => s.type === 'family').length > 0 && (
              <>
                <div className="sd-space-group-title">👨‍👩‍👧 家人私訊</div>
                {spaces.filter(s => s.type === 'family').map(space => (
                  <div
                    key={space.id}
                    className={`sd-space-item ${activeSpace === space.id ? 'active' : ''}`}
                    onClick={() => { setActiveSpace(space.id); setMobilePanel('chat'); }}
                  >
                    <div className="sd-space-info">
                      <div className="sd-space-name">{space.name}</div>
                      <div className="sd-space-meta-line">
                        <span className="sd-relationship">{space.relationship || '家人'}</span>
                        <span className="sd-chat-count">{space.todayChats || 0} 則</span>
                      </div>
                    </div>
                  </div>
                ))}
              </>
            )}

            {/* 其他 1對1 私訊 */}
            {spaces.filter(s => s.type === 'private').length > 0 && (
              <>
                <div className="sd-space-group-title">💬 其他私訊</div>
                {spaces.filter(s => s.type === 'private').map(space => (
                  <div
                    key={space.id}
                    className={`sd-space-item ${activeSpace === space.id ? 'active' : ''}`}
                    onClick={() => { setActiveSpace(space.id); setMobilePanel('chat'); }}
                  >
                    <div className="sd-space-info">
                      <div className="sd-space-name">{space.name}</div>
                      <div className="sd-space-meta-line">
                        <span className="sd-chat-count">{space.todayChats || 0} 則</span>
                      </div>
                    </div>
                  </div>
                ))}
              </>
            )}

            {spaces.length === 0 && (
              <div className="sd-empty-state">
                <p>尚無空間</p>
                <p className="sd-empty-hint">傳訊息給包容後，空間會自動出現</p>
              </div>
            )}
          </div>
        </aside>

        {/* Center - Main Content */}
        <main className={`sd-main-content ${mobilePanel === 'chat' ? 'mobile-active' : ''}`}>
          {/* Space Detail Header - 精簡版 */}
          <div className="sd-space-detail-header">
            <div className="sd-space-detail-info">
              <h2 className="sd-space-detail-name">
                {selectedSpace?.name || '選擇空間'}
                {selectedSpace?.type === 'group' && (
                  <span className="sd-member-badge">{selectedSpace?.memberCount || '?'} 人</span>
                )}
              </h2>
            </div>
            <div className="sd-space-detail-actions">
              <button className="sd-detail-btn" onClick={() => fetchConversations(activeSpace)}>
                刷新
              </button>
            </div>
          </div>

          {/* Content Tabs */}
          <div className="sd-content-tabs">
            <button
              className={`sd-content-tab ${activeTab === 'chat' ? 'active' : ''}`}
              onClick={() => setActiveTab('chat')}
            >
              對話記錄
            </button>
            <button
              className={`sd-content-tab ${activeTab === 'cognition' ? 'active' : ''}`}
              onClick={() => setActiveTab('cognition')}
            >
              認知狀態
            </button>
            <button
              className={`sd-content-tab ${activeTab === 'members' ? 'active' : ''}`}
              onClick={() => setActiveTab('members')}
            >
              成員
            </button>
            <button
              className={`sd-content-tab ${activeTab === 'summary' ? 'active' : ''}`}
              onClick={() => setActiveTab('summary')}
            >
              每日摘要
            </button>
          </div>

          {/* Content Area */}
          <div className="sd-content-area">
            {activeTab === 'chat' && (
              <div className="sd-chat-display">
                {chats.length === 0 ? (
                  <div className="sd-empty-state">
                    <span>💬</span>
                    <p>尚無對話記錄</p>
                  </div>
                ) : (
                  chats.map(chat => (
                    <div key={chat.id} className="sd-chat-item">
                      <div className="sd-chat-time">🕐 {chat.time}</div>

                      <div className="sd-chat-message">
                        <div className="sd-chat-avatar user">{chat.userMessage.avatar}</div>
                        <div className="sd-chat-bubble">
                          <div className="sd-chat-name">{chat.userMessage.name}</div>
                          <div className="sd-chat-text">{chat.userMessage.text}</div>
                        </div>
                      </div>

                      <div className="sd-process-section">
                        <div className="sd-process-title">🧠 處理流程</div>
                        <div className="sd-process-content">
                          {chat.process.tags.map((tag, i) => (
                            <span key={i}>
                              <span className={`sd-process-tag tag-${tag.type}`}>{tag.text}</span>
                              {i < chat.process.tags.length - 1 && ' → '}
                            </span>
                          ))}
                          <br />
                          💭 "{chat.process.thought}"
                        </div>
                      </div>

                      {chat.aiResponses.map((resp, i) => (
                        <div key={i} className="sd-chat-message" style={{ marginTop: i === 0 ? '0.75rem' : '0' }}>
                          <div className="sd-chat-avatar feli">🌸</div>
                          <div className="sd-chat-bubble feli">
                            <div className="sd-chat-name">包容</div>
                            <div className="sd-chat-text">{resp.text}</div>
                          </div>
                        </div>
                      ))}

                      <div className="sd-chat-stats">
                        <span className="sd-chat-stat">⏱️ {chat.stats.duration}</span>
                        <span className="sd-chat-stat">🎫 {chat.stats.tokens.toLocaleString()} tokens</span>
                        <span className="sd-chat-stat">💵 {chat.stats.cost}</span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {activeTab === 'cognition' && context && (
              <div className="sd-cognition-grid">
                <div className="sd-cognition-card">
                  <div className="sd-cognition-card-title">🏷️ 對象身份</div>
                  <div className="sd-cognition-card-content">
                    {context.cognition.identity}
                  </div>
                </div>
                <div className="sd-cognition-card">
                  <div className="sd-cognition-card-title">🎭 互動模式</div>
                  <div className="sd-cognition-card-content">
                    {context.cognition.mode}
                  </div>
                </div>
                <div className="sd-cognition-card">
                  <div className="sd-cognition-card-title">🗣️ 語言設定</div>
                  <div className="sd-cognition-card-content">
                    {context.cognition.language}
                  </div>
                </div>
                <div className="sd-cognition-card">
                  <div className="sd-cognition-card-title">⚠️ 禁忌事項</div>
                  <div className="sd-cognition-card-content">
                    {context.cognition.taboos}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'members' && (
              <div className="sd-members-display">
                {(selectedSpace?.type === 'multi_group' || selectedSpace?.type === 'duo_group') ? (
                  <div className="sd-members-container">
                    <div className="sd-members-header-bar">
                      <span>群組成員</span>
                      <span className="sd-members-count-badge">
                        {(context as any)?.members?.length || selectedSpace?.memberCount || '?'} 人
                      </span>
                    </div>
                    {(context as any)?.members?.length > 0 ? (
                      <div className="sd-members-grid">
                        {(context as any).members.map((member: any) => (
                          <div key={member.userId} className="sd-member-card">
                            <div className="sd-member-avatar-wrap">
                              {member.linePictureUrl ? (
                                <img
                                  src={member.linePictureUrl}
                                  alt={member.lineDisplayName || member.name}
                                  className="sd-member-avatar-img"
                                />
                              ) : (
                                <div className="sd-member-avatar-placeholder">
                                  {(member.lineDisplayName || member.name || '?')[0]}
                                </div>
                              )}
                            </div>
                            <div className="sd-member-details">
                              <div className="sd-member-line-name">
                                {member.lineDisplayName || member.name || '未知'}
                              </div>
                              {member.name && member.name !== member.lineDisplayName && (
                                <div className="sd-member-system-name">
                                  系統名稱: {member.name}
                                </div>
                              )}
                              {member.relationship && (
                                <div className="sd-member-relationship">
                                  {member.relationship}
                                </div>
                              )}
                              <div className="sd-member-stats">
                                <span>{member.messageCount || 0} 則訊息</span>
                                <span>·</span>
                                <span>{member.lastSeenAgo}</span>
                              </div>
                              {member.lineUserId && (
                                <div className="sd-member-line-id">
                                  ID: {member.lineUserId.substring(0, 10)}...
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="sd-members-note">
                        成員資料會在他們發言時自動取得
                      </div>
                    )}
                  </div>
                ) : selectedSpace?.type === 'family' || selectedSpace?.type === 'private' ? (
                  <div className="sd-members-container">
                    <div className="sd-members-header-bar">
                      <span>私訊對象</span>
                    </div>
                    <div className="sd-member-card">
                      <div className="sd-member-details">
                        <div className="sd-member-line-name">{selectedSpace.name}</div>
                        {selectedSpace.relationship && (
                          <div className="sd-member-relationship">{selectedSpace.relationship}</div>
                        )}
                        <div className="sd-member-stats">
                          1 對 1 私訊空間
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="sd-empty-state">
                    <p>選擇空間查看成員</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'summary' && (
              <div className="sd-summary-display">
                <div className="sd-summary-header">
                  <span>📊 每日對話摘要</span>
                  <button
                    className="sd-btn sd-generate-summary-btn"
                    onClick={handleGenerateSummary}
                    disabled={isGeneratingSummary}
                  >
                    {isGeneratingSummary ? '生成中...' : '生成今日摘要'}
                  </button>
                </div>

                {summaries.length === 0 ? (
                  <div className="sd-empty-state">
                    <span>📝</span>
                    <p>尚無每日摘要</p>
                    <p className="sd-empty-hint">點擊「生成今日摘要」按鈕手動生成</p>
                    <p className="sd-empty-hint">或等待每天 22:30 自動生成</p>
                  </div>
                ) : (
                  <div className="sd-summary-list">
                    {summaries.map(s => (
                      <div key={s.id} className="sd-summary-card">
                        <div className="sd-summary-card-header">
                          <span className="sd-summary-date">{s.date}</span>
                          <span className={`sd-summary-mood mood-${s.mood}`}>
                            {getMoodEmoji(s.mood)} {s.mood}
                          </span>
                        </div>
                        <div className="sd-summary-stats-row">
                          <span>💬 {s.messageCount} 則訊息</span>
                          <span>👥 {s.participantCount} 人參與</span>
                        </div>
                        <div className="sd-summary-text">{s.summary}</div>

                        {s.highlights && s.highlights.length > 0 && (
                          <div className="sd-summary-highlights">
                            <div className="sd-summary-highlights-title">✨ 重點事件</div>
                            <ul>
                              {s.highlights.map((h, i) => (
                                <li key={i}>{h}</li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {s.topics && s.topics.length > 0 && (
                          <div className="sd-summary-topics">
                            {s.topics.map((t, i) => (
                              <span key={i} className="sd-summary-topic-tag">#{t}</span>
                            ))}
                          </div>
                        )}

                        <div className="sd-summary-footer">
                          <span>生成於 {new Date(s.generatedAt).toLocaleString('zh-TW')}</span>
                          <span className="sd-summary-by">
                            {s.generatedBy === 'manual' ? '🖐️ 手動' : '🤖 自動'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </main>

        {/* Right Sidebar - Context */}
        <aside className={`sd-context-sidebar ${mobilePanel === 'context' || mobilePanel === 'alert' ? 'mobile-active' : ''}`}>
          <div className="sd-context-header">
            <div className="sd-context-title">
              <span>📦</span> 空間上下文
            </div>
          </div>

          <div className="sd-context-content">
            {/* Space Status */}
            {context && (
              <>
                <div className="sd-context-section">
                  <div className="sd-context-section-title">📊 空間狀態</div>
                  <div className="sd-status-row">
                    <span className="sd-status-label">包容狀態</span>
                    <span className={`sd-status-value ${context.status === 'awake' ? 'good' : ''}`}>
                      {context.status === 'awake' ? '☀️ 清醒' :
                       context.status === 'dreaming' ? '🌙 作夢中' : '😴 睡眠中'}
                    </span>
                  </div>
                  <div className="sd-status-row">
                    <span className="sd-status-label">上次對話</span>
                    <span className="sd-status-value">{context.lastChat}</span>
                  </div>
                  <div className="sd-status-row">
                    <span className="sd-status-label">今日對話</span>
                    <span className="sd-status-value">{context.todayChats} 次</span>
                  </div>
                  <div className="sd-status-row">
                    <span className="sd-status-label">情感溫度</span>
                    <span className={`sd-status-value ${context.emotionalTemp > 0.7 ? 'good' : ''}`}>
                      {context.emotionalTemp.toFixed(2)}
                    </span>
                  </div>
                </div>

                {/* Alerts */}
                {alerts.length > 0 && (
                  <div className="sd-context-section">
                    <div className="sd-context-section-title sd-alert-title">🚨 異常偵測</div>
                    {alerts.map((alert) => (
                      <div key={alert.id} className="sd-context-item sd-alert-item">
                        <div className="sd-alert-header">
                          <span className="sd-alert-type">⚠️ {alert.type}</span>
                          <span className="sd-alert-source">{alert.source} · {alert.time}</span>
                        </div>
                        <div className="sd-alert-message">
                          {alert.message}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Skills */}
                <div className="sd-context-section">
                  <div className="sd-context-section-title">🧩 啟用技能</div>
                  <div className="sd-skills-mini">
                    {context.skills.map((skill, i) => (
                      <div key={i} className="sd-skill-tag">
                        <span className={`sd-skill-dot ${skill.active ? 'active' : ''}`}></span>
                        {skill.name}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Dream Seed */}
                {context.dreamSeed && (
                  <div className="sd-context-section">
                    <div className="sd-context-section-title">🌱 今日夢境種子</div>
                    <div className="sd-context-item sd-dream-item">
                      {context.dreamSeed.content}
                      <div className="sd-dream-status">
                        {context.dreamSeed.used ? '✅ 已使用' : '⬜ 尚未使用'}
                      </div>
                    </div>
                  </div>
                )}

                {/* Rules */}
                {context.rules && context.rules.length > 0 && (
                  <div className="sd-context-section">
                    <div className="sd-context-section-title sd-warning-title">⚠️ 空間鐵則</div>
                    <div className="sd-context-item sd-rule-item">
                      {context.rules.map((rule, i) => (
                        <div key={i} className={rule.includes('❌') ? 'sd-rule-danger' : ''}>
                          → {rule}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="sd-footer">
            Made with <span className="sd-footer-heart">🤍</span> for 包容
          </div>
        </aside>
      </div>

      {/* Mobile Bottom Navigation */}
      <nav className="sd-mobile-nav">
        <button
          className={`sd-mobile-nav-btn ${mobilePanel === 'spaces' ? 'active' : ''}`}
          onClick={() => setMobilePanel('spaces')}
        >
          <span className="sd-mobile-nav-icon">🌐</span>
          <span className="sd-mobile-nav-label">空間</span>
        </button>
        <button
          className={`sd-mobile-nav-btn ${mobilePanel === 'chat' ? 'active' : ''}`}
          onClick={() => setMobilePanel('chat')}
        >
          <span className="sd-mobile-nav-icon">💬</span>
          <span className="sd-mobile-nav-label">對話</span>
        </button>
        <button
          className={`sd-mobile-nav-btn ${mobilePanel === 'context' ? 'active' : ''}`}
          onClick={() => setMobilePanel('context')}
        >
          <span className="sd-mobile-nav-icon">📦</span>
          <span className="sd-mobile-nav-label">上下文</span>
        </button>
        <button
          className={`sd-mobile-nav-btn ${mobilePanel === 'alert' ? 'active' : ''}`}
          onClick={() => setMobilePanel('alert')}
          style={{ position: 'relative' }}
        >
          <span className="sd-mobile-nav-icon">🚨</span>
          <span className="sd-mobile-nav-label">異常</span>
          {alertCount > 0 && <span className="sd-mobile-alert-badge">{alertCount}</span>}
        </button>
      </nav>
    </div>
  );
}
